/**
 * @file PdfPreviewDialog.jsx
 * @description Shows a PDF the server builds (a machine's invoice / work log)
 *              inside the page, with a Download button — instead of saving a
 *              file the user has not seen yet.
 *
 * `doc` = { url, title, fallbackName, emptyMessage } opens it; null closes it.
 * The request carries the JWT the same way apiClient does. A 204 from the
 * server means it had nothing to put in the document (e.g. the machine did
 * not work in those dates) and is shown as `emptyMessage`, not as an error.
 */
import React, { useEffect, useState } from 'react';
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Alert, Typography,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';

const EMPTY = { loading: false, error: '', src: null, name: '' };

const PdfPreviewDialog = ({ doc, onClose }) => {
    const [state, setState] = useState(EMPTY);

    useEffect(() => {
        if (!doc) { setState(EMPTY); return undefined; }
        let alive = true;
        let objectUrl = null;
        setState({ ...EMPTY, loading: true });
        (async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(doc.url, { headers: token ? { 'x-auth-token': token } : {} });
                if (res.status === 204) throw new Error(doc.emptyMessage || 'There is nothing to show for these dates.');
                if (!res.ok) throw new Error(`The document could not be built (error ${res.status}).`);
                const blob = await res.blob();
                if (!(await blob.slice(0, 4).text()).startsWith('%PDF')) throw new Error('The server did not return a PDF.');
                const m = (res.headers.get('content-disposition') || '').match(/filename="?([^"]+)"?/);
                // Typed explicitly so the browser's PDF viewer renders it in the frame.
                objectUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                if (alive) setState({ loading: false, error: '', src: objectUrl, name: m ? m[1] : doc.fallbackName });
            } catch (e) {
                if (alive) setState({ ...EMPTY, error: e.message || 'The document could not be loaded.' });
            }
        })();
        return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [doc]);

    const download = () => {
        const a = document.createElement('a');
        a.href = state.src;
        a.download = state.name || 'document.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    return (
        <Dialog open={!!doc} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '88vh' } }}>
            <DialogTitle sx={{ fontWeight: 800 }}>{doc?.title}</DialogTitle>
            <DialogContent dividers sx={{ p: 0, display: 'flex' }}>
                {state.loading ? (
                    <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1.5}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">Building the document…</Typography>
                    </Box>
                ) : state.error ? (
                    <Box flex={1} p={3}><Alert severity="info">{state.error}</Alert></Box>
                ) : state.src ? (
                    <iframe title={doc?.title || 'Document'} src={state.src} style={{ border: 0, width: '100%', height: '100%' }} />
                ) : null}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>Close</Button>
                <Button variant="contained" startIcon={<DownloadIcon />} disabled={!state.src} onClick={download}>
                    Download
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PdfPreviewDialog;
